import { useEffect, useState } from "react";
import { Mail, Loader2, X, ArrowDown, ArrowUp, RefreshCw, ChevronRight, Paperclip, FileText, Reply, PenSquare } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Modal } from "@/components/common";
import { api } from "@/lib/api";
import MailSchreibenModal from "@/components/MailSchreibenModal";
import GoogleBilderHinweis from "@/components/GoogleBilderHinweis";

/**
 * MailHistoryModal — zeigt alle Mails (ein-/ausgehend) eines Kunden
 * aus allen aktiven IMAP-Postfächern.
 * Props: { isOpen, onClose, email, kundeName?, kundeId?, projekte?, projektId? }
 * Mit kundeId: Knöpfe „Neue Mail“ / „Antworten“ (Mail-Fenster, A-010 M1).
 */
const MailHistoryModal = ({ isOpen, onClose, email, kundeName = "", kundeId = "", projekte = [], projektId = "" }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(0);
  const [adressen, setAdressen] = useState([]);  // alle Adressen des Kunden (A-018), Haupt-Mail zuerst
  const [detail, setDetail] = useState(null);  // expanded mail
  const [detailLoading, setDetailLoading] = useState(false);
  const [maxCount, setMaxCount] = useState(30);
  const [schreiben, setSchreiben] = useState(null);  // null | { mail: null|Eintrag }

  const load = async (max = maxCount) => {
    if (!email) return;
    setLoading(true);
    setDetail(null);
    try {
      const r = await api.post("/module-mail-inbox/customer-mails", { email, kunde_id: kundeId || undefined, max_count: max });
      setItems(r.data?.items || []);
      setAdressen(r.data?.adressen || []);
      statusLaden(r.data?.items || []);
      setCount(r.data?.count || 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mailverlauf laden fehlgeschlagen");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && email) load(maxCount);
    // eslint-disable-next-line
  }, [isOpen, email, kundeId, maxCount]);

  // ── Mail-Anhänge ins Projekt (Stufe 1, 27.09.2026) ──
  const navigate = useNavigate();
  const [anh, setAnh] = useState(null);
  const [anhLaden, setAnhLaden] = useState(false);
  const [zielProjekt, setZielProjekt] = useState("");
  const [auswahl, setAuswahl] = useState({});
  const [anhBusy, setAnhBusy] = useState(false);
  const [fertig, setFertig] = useState(null);
  const [zeigeSig, setZeigeSig] = useState(false);
  // Stufe 1c (27.09.2026): was wurde aus welcher Mail schon übernommen?
  const [uebStatus, setUebStatus] = useState({});
  const [unterschrieben, setUnterschrieben] = useState(false);
  const statusLaden = async (liste) => {
    const mails = (liste || [])
      .filter((x) => x.anhaenge > 0)
      .map((x) => ({ account_id: x.account_id, folder: x.folder, uid: x.uid, message_id: x.message_id || "" }));
    if (!mails.length) {
      setUebStatus({});
      return;
    }
    try {
      const r = await api.post("/module-mail-inbox/mail-anhaenge/status", { mails });
      setUebStatus(r.data?.uebernommen || {});
    } catch {
      /* Anzeige ist nur eine Hilfe – bei Fehler einfach weglassen */
    }
  };

  const detailKey = detail?._meta ? `${detail._meta.account_id}/${detail._meta.folder}/${detail._meta.uid}` : "";
  const anzahlGewaehlt = Object.values(auswahl).filter(Boolean).length;
  const zielTitel = (anh?.projekte || []).find((p) => p.id === zielProjekt)?.titel || "";

  const standardAuswahl = (daten, projektId) => {
    const doppelt = new Set(((daten?.duplikate || {})[projektId]) || []);
    const a = {};
    (daten?.anhaenge || []).forEach((x) => {
      a[x.index] = x.art !== "sonstig" && !x.zu_gross && !x.klein && !doppelt.has(x.index) && !x.eigenes_dokument && !daten?.eigene_mail;
    });
    return a;
  };

  const anhaengePruefen = async (it, behalteZiel = "") => {
    setAnhLaden(true);
    try {
      const r = await api.post("/module-mail-inbox/mail-anhaenge/pruefen", {
        account_id: it.account_id, folder: it.folder, uid: it.uid, email,
      });
      const aktive = (r.data?.projekte || []).filter((p) => !p.archiv);
      const pid = behalteZiel || (aktive.length === 1 ? aktive[0].id : "");  // bei mehreren Projekten bewusst auswählen lassen
      setAnh({ ...r.data, _key: `${it.account_id}/${it.folder}/${it.uid}` });
      setZielProjekt(pid);
      setAuswahl(standardAuswahl(r.data, pid));
      setUnterschrieben(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anhänge prüfen fehlgeschlagen");
    } finally {
      setAnhLaden(false);
    }
  };

  const projektWechseln = (pid) => {
    setZielProjekt(pid);
    setAuswahl(standardAuswahl(anh, pid));
  };

  const uebernehmen = async () => {
    const indices = Object.keys(auswahl).filter((k) => auswahl[k]).map(Number);
    if (!indices.length || !zielProjekt || !detail?._meta) return;
    const it = detail._meta;
    setAnhBusy(true);
    try {
      const r = await api.post("/module-mail-inbox/mail-anhaenge/uebernehmen", {
        account_id: it.account_id, folder: it.folder, uid: it.uid, projekt_id: zielProjekt, indices, unterschrieben,
      });
      toast.success(`${r.data.anzahl} Datei(en) übernommen`);
      await anhaengePruefen(it, zielProjekt);
      setFertig(r.data);
      statusLaden(items);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Übernahme fehlgeschlagen");
    } finally {
      setAnhBusy(false);
    }
  };

  const rueckgaengig = async () => {
    if (!fertig || !detail?._meta) return;
    if (!window.confirm(`Die ${fertig.anzahl} eben übernommenen Datei(en) wieder aus dem Projekt „${fertig.projekt_titel}" entfernen?`)) return;
    setAnhBusy(true);
    try {
      await api.post("/module-mail-inbox/mail-anhaenge/rueckgaengig", {
        projekt_id: fertig.projekt_id, uebernahme_id: fertig.uebernahme_id,
      });
      toast.success("Übernahme rückgängig gemacht");
      setFertig(null);
      await anhaengePruefen(detail._meta, zielProjekt);
      statusLaden(items);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Rückgängig fehlgeschlagen");
    } finally {
      setAnhBusy(false);
    }
  };

  const openDetail = async (it) => {
    setAnh(null);
    setFertig(null);
    setDetail({ loading: true, _key: `${it.account_id}/${it.folder}/${it.uid}` });
    setDetailLoading(true);
    try {
      const r = await api.post("/module-mail-inbox/mail-detail", {
        account_id: it.account_id,
        folder: it.folder,
        uid: it.uid,
      });
      setDetail({ ...r.data, _meta: it });
      anhaengePruefen(it);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Mail-Detail laden fehlgeschlagen");
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Mailverlauf · ${kundeName || email}`} size="xl">
      <div className="space-y-3" data-testid="mail-history-modal">
        <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
          <div className="text-muted-foreground min-w-0" data-testid="history-kopf">
            <span className="break-all" data-testid="history-adressen">
              {adressen.length > 1
                ? adressen.map((a, i) => (
                    <span key={a.adresse}>{i > 0 && " · "}{a.adresse}{a.gefunden_ueber && a.gefunden_ueber !== "Haupt-Mail" ? ` (${a.gefunden_ueber})` : ""}</span>
                  ))
                : email}
            </span>
            {" "}· letzte 26 Wochen · max {maxCount} Mails {!loading && `· ${count} gefunden`}
          </div>
          <div className="flex gap-1">
            {kundeId && (
              <button
                onClick={() => setSchreiben({ mail: null })}
                className="px-2.5 py-1 text-xs rounded-sm bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1"
                data-testid="btn-history-neue-mail"
              >
                <PenSquare className="w-3.5 h-3.5" /> Neue Mail
              </button>
            )}
            {[15, 30, 60].map((n) => (
              <button
                key={n}
                onClick={() => setMaxCount(n)}
                className={`px-2.5 py-1 text-xs rounded-sm ${maxCount === n ? "bg-primary text-primary-foreground" : "border hover:bg-accent"}`}
                data-testid={`btn-history-max-${n}`}
              >
                max {n}
              </button>
            ))}
            <button
              onClick={() => load(maxCount)}
              className="px-2.5 py-1 text-xs rounded-sm border hover:bg-accent flex items-center gap-1"
              title="Neu laden"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> IMAP-Suche läuft (5-15 Sek)…
          </div>
        ) : items.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center">
            Keine Mails von/an {adressen.length > 1 ? "diesen Adressen" : email} in den letzten 26 Wochen gefunden.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-3 max-h-[60vh]">
            {/* Liste links */}
            <div className="lg:col-span-2 overflow-auto space-y-1 pr-1">
              {items.map((it) => {
                const key = `${it.account_id}/${it.folder}/${it.uid}`;
                const isActive = detail?._key === key || detail?._meta?.uid === it.uid;
                return (
                  <button
                    key={key}
                    onClick={() => openDetail(it)}
                    className={`w-full text-left border rounded p-2 text-xs hover:bg-accent/60 ${isActive ? "border-primary bg-primary/5" : ""}`}
                    data-testid={`history-row-${it.uid}`}
                  >
                    <div className="flex items-center gap-2 flex-wrap">
                      {it.direction === "ein" ? (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-100 text-blue-800 text-[10px]">
                          <ArrowDown className="w-2.5 h-2.5" /> ein
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 text-[10px]">
                          <ArrowUp className="w-2.5 h-2.5" /> aus
                        </span>
                      )}
                      <span className="text-[10px] text-muted-foreground">{it.account_label}</span>
                      {it.anhaenge > 0 && (() => {
                        const ueb = uebStatus[`${it.account_id}/${it.folder}/${it.uid}`] || 0;
                        const alles = ueb >= it.anhaenge;
                        return (
                          <span
                            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] ${alles ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200" : "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200"}`}
                            title={alles ? "Alle Anhänge übernommen" : ueb ? `${ueb} von ${it.anhaenge} Anhängen übernommen` : "Mail hat Anhänge (ohne Signatur-Logos)"}
                            data-testid="history-anhaenge"
                          >
                            <Paperclip className="w-2.5 h-2.5" /> {ueb && !alles ? `${ueb}/${it.anhaenge}` : it.anhaenge}{alles ? " ✓" : ""}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="font-medium mt-1 truncate">{it.subject || "(kein Betreff)"}</div>
                    <div className="text-muted-foreground truncate">
                      {it.direction === "ein" ? `von ${it.from_email}` : `an ${it.to_email}`}
                    </div>
                    {it.gefunden_ueber && (
                      <div className="mt-0.5 text-[10px] text-amber-800 dark:text-amber-300 truncate" data-testid={`history-gefunden-${it.uid}`}>
                        gefunden über: {it.gefunden_ueber}
                      </div>
                    )}
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {it.date?.slice(0, 16).replace("T", " ")}
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Detail rechts */}
            <div className="lg:col-span-3 border-l lg:pl-3 overflow-auto">
              {!detail ? (
                <div className="text-sm text-muted-foreground py-8 text-center flex flex-col items-center gap-2">
                  <ChevronRight className="w-5 h-5" />
                  Wähle eine Mail aus der Liste links.
                </div>
              ) : detailLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
                  <Loader2 className="w-4 h-4 animate-spin" /> Lade Mail…
                </div>
              ) : (
                <div className="space-y-2 text-sm">
                  {kundeId && detail._meta && (
                    <div className="flex justify-end">
                      <button
                        onClick={() => setSchreiben({ mail: detail._meta })}
                        className="px-2.5 py-1 text-xs rounded-sm bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1"
                        data-testid="btn-history-antworten"
                      >
                        <Reply className="w-3.5 h-3.5" /> Antworten
                      </button>
                    </div>
                  )}
                  <div>
                    <div className="text-xs text-muted-foreground">Betreff</div>
                    <div className="font-semibold break-words">{detail.subject || "(kein Betreff)"}</div>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <div className="text-muted-foreground">Von</div>
                      <div className="break-all">{detail.from_email || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">An</div>
                      <div className="break-all">{detail.to_email || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Datum</div>
                      <div>{detail.date?.slice(0, 16).replace("T", " ") || "—"}</div>
                    </div>
                    <div>
                      <div className="text-muted-foreground">Postfach</div>
                      <div>{detail.account_label || "—"}</div>
                    </div>
                  </div>
                  <div className="border-t pt-2">
                    <div className="text-xs text-muted-foreground mb-1">Inhalt</div>
                    <pre className="text-xs whitespace-pre-wrap bg-muted/40 rounded-sm p-3 border max-h-[40vh] overflow-auto break-words">
                      {detail.body || "(leer)"}
                    </pre>
                  </div>

                  {/* Mail-Anhänge ins Projekt (Stufe 1, 27.09.2026) */}
                  <div className="border-t pt-2 space-y-2" data-testid="mail-anhaenge">
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <Paperclip className="w-3.5 h-3.5" /> Anhänge
                      {anhLaden && <Loader2 className="w-3.5 h-3.5 animate-spin ml-1" />}
                    </div>
                    {anh && anh._key === detailKey && (anh.google_drive || []).length > 0 && (
                      <GoogleBilderHinweis links={anh.google_drive}
                        kunde={kundeId ? { id: kundeId, name: kundeName, email } : null}
                        projekt={(anh.projekte || []).find((p) => p.id === zielProjekt) || null} />
                    )}
                    {anh && anh._key === detailKey && ((anh.anhaenge || []).length === 0 ? (
                      (anh.google_drive || []).length === 0 && <div className="text-xs text-muted-foreground">Keine Anhänge.</div>
                    ) : (
                      <div className="space-y-2">
                        {anh.eigene_mail && (
                          <div className="text-xs border border-border rounded-sm p-2 bg-muted/40 text-muted-foreground" data-testid="anhaenge-eigene-mail">
                            Eigene Mail – die Anhänge (Angebote, Auftragsbestätigungen, Rechnungen) liegen bereits in der Suite und werden nicht übernommen.
                          </div>
                        )}
                        {(anh.projekte || []).length === 0 ? (
                          <div className="text-xs text-amber-700 dark:text-amber-300">Für diesen Kunden gibt es noch kein Projekt – bitte zuerst ein Projekt anlegen.</div>
                        ) : (
                          <div className="space-y-1" data-testid="anhaenge-projekt">
                            <div className="text-xs text-muted-foreground">
                              In welches Projekt?{" "}
                              {!zielProjekt && <span className="text-amber-700 dark:text-amber-300 font-medium">Bitte auswählen</span>}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {anh.projekte.map((p) => (
                                <button
                                  type="button"
                                  key={p.id}
                                  onClick={() => projektWechseln(p.id)}
                                  className={`text-left border rounded-sm px-2 py-1.5 text-xs ${zielProjekt === p.id ? "border-emerald-600 ring-1 ring-emerald-600 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100" : "border-border bg-background text-foreground hover:bg-muted"} ${p.archiv ? "opacity-60" : ""}`}
                                  data-testid={`anhaenge-projekt-${p.id}`}
                                >
                                  <div className="font-medium truncate">{zielProjekt === p.id ? "✓ " : ""}{p.titel || "(ohne Titel)"}</div>
                                  <div className="text-muted-foreground truncate">
                                    {[p.status, p.erstellt, `${p.bilder_anzahl} Bilder`, p.kunde_name].filter(Boolean).join(" · ")}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {anh.anhaenge.filter((a) => !a.signatur || zeigeSig).map((a) => {
                            const doppelt = ((anh.duplikate || {})[zielProjekt] || []).includes(a.index);
                            const moeglich = a.art !== "sonstig" && !a.zu_gross && !anh.eigene_mail;
                            return (
                              <label key={a.index} className={`border border-border rounded-sm p-1.5 text-[11px] flex flex-col gap-1 ${moeglich ? "cursor-pointer" : "opacity-60"}`}>
                                <div className="flex items-center gap-1 min-w-0">
                                  <input
                                    type="checkbox"
                                    disabled={!moeglich}
                                    checked={!!auswahl[a.index]}
                                    onChange={(e) => setAuswahl({ ...auswahl, [a.index]: e.target.checked })}
                                  />
                                  <span className="truncate" title={a.filename}>{a.filename}</span>
                                </div>
                                {a.vorschau ? (
                                  <img src={a.vorschau} alt="" className="w-full h-20 object-cover rounded-sm" />
                                ) : (
                                  <div className="h-20 flex items-center justify-center bg-muted/40 rounded-sm">
                                    <FileText className="w-6 h-6 text-muted-foreground" />
                                  </div>
                                )}
                                <div className="text-muted-foreground">{Math.round((a.size || 0) / 1024)} KB{a.klein ? " · vermutlich Logo" : ""}</div>
                                {doppelt && <div className="text-amber-700 dark:text-amber-300 font-medium">schon im Projekt</div>}
                                {a.eigenes_dokument && <div className="text-muted-foreground">Eigenes Dokument{a.dokument_nr ? ` (${a.dokument_nr})` : ""} – liegt bereits in der Suite</div>}
                                {a.zu_gross && <div className="text-muted-foreground">zu groß (max 15 MB)</div>}
                                {a.art === "sonstig" && <div className="text-muted-foreground">kein Bild/PDF</div>}
                              </label>
                            );
                          })}
                        </div>
                        {anh.anhaenge.some((a) => a.signatur) && (
                          <button type="button" onClick={() => setZeigeSig(!zeigeSig)} className="text-xs underline text-muted-foreground" data-testid="btn-signaturen">
                            {zeigeSig
                              ? "Vermutliche Signaturen wieder ausblenden"
                              : `${anh.anhaenge.filter((a) => a.signatur).length} weitere Datei(en) (vermutlich Signatur/Logo) anzeigen`}
                          </button>
                        )}
                        {(anh.projekte || []).length > 0 && !anh.eigene_mail && (
                          <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="anhaenge-unterschrieben">
                            <input type="checkbox" checked={unterschrieben} onChange={(e) => setUnterschrieben(e.target.checked)} />
                            <span>✍ Unterschriebenes Dokument (Angebot/Auftrag unterschrieben zurück)</span>
                          </label>
                        )}
                        {(anh.projekte || []).length > 0 && !anh.eigene_mail && (
                          <button
                            onClick={uebernehmen}
                            disabled={anhBusy || anzahlGewaehlt === 0 || !zielProjekt}
                            className="w-full px-3 py-2 text-sm bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50"
                            data-testid="btn-anhaenge-uebernehmen"
                          >
                            {anhBusy
                              ? "Bitte warten – Dateien werden einzeln verarbeitet…"
                              : !zielProjekt
                              ? "Bitte zuerst oben ein Projekt auswählen"
                              : `${anzahlGewaehlt} Datei(en) ins Projekt „${zielTitel || "ohne Titel"}" übernehmen`}
                          </button>
                        )}
                        {fertig && (
                          <div className="border border-emerald-300 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200 rounded-sm p-2 text-xs space-y-2" data-testid="anhaenge-fertig">
                            <div>
                              ✅ {fertig.anzahl} Datei(en) ins Projekt „{fertig.projekt_titel || "ohne Titel"}" übernommen
                              {(fertig.uebersprungen || []).length ? ` · ${fertig.uebersprungen.length} übersprungen` : ""}.
                            </div>
                            {(fertig.uebersprungen || []).map((u, i) => (
                              <div key={i}>– {u.filename}: {u.grund}</div>
                            ))}
                            <div className="flex flex-wrap items-center gap-2">
                              <span>Wohin jetzt?</span>
                              <button onClick={() => { onClose?.(); navigate(`/module/projekte/werkbank/${fertig.kunde_id}`); }} className="px-2 py-1 border border-border rounded-sm bg-background text-foreground hover:bg-muted">Zum Projekt</button>
                              <button onClick={() => { onClose?.(); navigate(`/module/kunden?edit=${fertig.kunde_id}`); }} className="px-2 py-1 border border-border rounded-sm bg-background text-foreground hover:bg-muted">Zum Kunden</button>
                              <button onClick={() => setFertig(null)} className="px-2 py-1 border border-border rounded-sm bg-background text-foreground hover:bg-muted">Hier bleiben</button>
                              {fertig.anzahl > 0 && (
                                <button onClick={rueckgaengig} disabled={anhBusy} className="px-2 py-1 border border-red-300 text-red-700 dark:text-red-300 rounded-sm hover:bg-red-50 dark:hover:bg-red-950/40">Rückgängig</button>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <MailSchreibenModal
        isOpen={!!schreiben}
        onClose={() => setSchreiben(null)}
        kundeId={kundeId}
        mail={schreiben?.mail || null}
        projekte={projekte}
        projektId={projektId}
        onGesendet={() => load(maxCount)}
      />
    </Modal>
  );
};

export default MailHistoryModal;
